import { CellContext, ColumnHelper, defineColumns, TanstackTable } from '@thingslog/ui-components';
import React, { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { scheduledEventsQueryClient } from '../../utils/ReactQueryClients';
import { PageHeader, PageHeaderControls, PortalPageShell } from '../../components';
import { CommandState, ScheduledEventCommandExecutionInfoDto } from '@thingslog/repositories';
import { format } from 'date-fns';
import { CommandStatusBox } from './components/CommandStatusBox';

const CommandTaskOverview: FC<CommandTaskOverviewProps> = ({}: CommandTaskOverviewProps) => {
  const { t } = useTranslation();
  const { scheduledEventId, scheduledEventLogId } = useParams();
  const { useGetScheduledEventLogDetail } = useMemo(() => scheduledEventsQueryClient, []);

  const headerOptions: PageHeaderControls = {
    showBackButton: true,
    showLanguageButton: true,
    showLogoutButton: true,
    showRedirectThingslogButton: true,
  };

  const { data: eventLogDetailsData, isLoading } = useGetScheduledEventLogDetail(
    Number(scheduledEventId),
    Number(scheduledEventLogId),
    {
      enabled: !!scheduledEventId && !!scheduledEventLogId,
      refetchOnWindowFocus: false,
    }
  );

  const columns = useMemo(
    () =>
      defineColumns<ScheduledEventCommandExecutionInfoDto>(
        (column: ColumnHelper<ScheduledEventCommandExecutionInfoDto>) => {
          const boldHeader = (label: string): ReactNode => (
            <div className="font-semibold">{label}</div>
          );

          return [
            column.accessor('deviceName', {
              header: (): ReactNode => boldHeader(t('device_name')),
              meta: { filterVariant: 'text', label: t('device_name'), grow: true, minWidth: 150 },
            }),
            column.accessor('deviceNumber', {
              header: (): ReactNode => boldHeader(t('device_number')),
              meta: { filterVariant: 'text', label: t('device_number'), grow: true, minWidth: 110 },
            }),
            column.accessor('scheduledEventName', {
              header: (): ReactNode => boldHeader(t('task_overview_scheduled_event_name')),
              meta: {
                filterVariant: 'text',
                label: t('task_overview_scheduled_event_name'),
                grow: true,
                minWidth: 110,
              },
            }),
            column.accessor('desiredState', {
              header: (): ReactNode => boldHeader(t('task_overview_desired_state')),
              meta: {
                filterVariant: 'text',
                label: t('task_overview_desired_state'),
                grow: true,
                minWidth: 110,
              },
            }),
            column.accessor('commandState', {
              header: (): ReactNode => boldHeader(t('task_overview_command_state')),
              meta: {
                filterVariant: 'multiSelect',
                label: t('task_overview_command_state'),
                grow: true,
                minWidth: 110,
              },
              cell: (
                info: CellContext<ScheduledEventCommandExecutionInfoDto, CommandState>
              ): ReactNode => <CommandStatusBox commandState={info.getValue() || null} />,
            }),
            column.accessor('executionTime', {
              header: (): ReactNode => boldHeader(t('scheduled_task_event_form_execution_time')),
              meta: {
                filterVariant: 'date',
                label: t('scheduled_task_event_form_execution_time'),
                grow: true,
                minWidth: 110,
              },
              cell: (info: CellContext<ScheduledEventCommandExecutionInfoDto, Date>): ReactNode => {
                const value = info.getValue();
                return value ? format(new Date(value), 'dd MMM yyyy HH:mm') : '';
              },
            }),
          ];
        }
      ),
    [t]
  );

  return (
    <>
      <PageHeader
        title={t('task_scheduler_command_task_overview')}
        options={headerOptions}
        mode="sticky"
      />
      <PortalPageShell maxWidth="form" className="flex flex-col">
        <TanstackTable<ScheduledEventCommandExecutionInfoDto>
          mode="client"
          className="w-full"
          classNames={{ container: 'max-h-[calc(100dvh_-_10rem)]' }}
          pageSizeOptions={[10, 25, 50]}
          density="compact"
          isLoading={isLoading}
          getRowId={(row: ScheduledEventCommandExecutionInfoDto): string =>
            row.deviceNumber + row.deviceName + row.desiredState
          }
          data={eventLogDetailsData?.scheduledEventsExecutionInfo || []}
          columns={columns}
          initialState={{ pagination: { pageIndex: 0, pageSize: 10 } }}
          emptyMessage={t('task_overview_no_data', 'No command executions')}
        />
      </PortalPageShell>
    </>
  );
};

interface CommandTaskOverviewProps {}

export default CommandTaskOverview;
